import { id } from "date-fns/locale";
import { TTargetProduct } from "types/product";
import { TTargetTax } from "types/tax";

type Total = {
  list: number;
  sale: number;
  install: number;
};

export function reorderTaxes(taxes: TTargetTax[]): TTargetTax[] {
  const groupOrder: Array<TTargetTax["tax_type"]> = ["discount", "fee", "tax"];
  
  const sorted = groupOrder.reduce<TTargetTax[]>((acc, type) => {
    const group = taxes
      .filter(t => t.tax_type === type)
      .sort((a,b) => (a.ordering || 0) - (b.ordering || 0));
    return acc.concat(group);
  }, []);
  
  return sorted.map((tax, idx) => ({
    ...tax,
    ordering: idx + 1,
  }));
}

export const calculateTotal = (array?: TTargetProduct[]) => {
  return (
    (array &&
      array.reduce(
        (prev, product) => ({
          list: prev.list + product.quantity * +(product.list_price || 0),
          sale: prev.sale + product.quantity * +(product.unit_price || 0),
          install:
            prev.install + product.quantity * +(product.install_price || 0),
        }),
        {
          list: 0,
          sale: 0,
          install: 0,
        }
      )) || {
      list: 0,
      sale: 0,
      install: 0,
    }
  );
};

export const calculateGrandTotal = (total: Total, taxTotal: Total) => ({
  list: total.list + taxTotal.list,
  sale: total.sale + taxTotal.sale,
  install: total.install + taxTotal.install,
});

export const calculateTaxTotal = (
  products: TTargetProduct[] | null | undefined,
  taxes: TTargetTax[] | null | undefined,
  isEdit: boolean = false
): { list: number; sale: number; install: number } => {
  // Normalize inputs
  const prods = Array.isArray(products) ? products : [];
  const allTaxes = Array.isArray(taxes) ? taxes : [];

  // When isEdit=true: products are in DOLLARS, tax rates are in CENTS
  // When isEdit=false: products are in CENTS, tax rates are in CENTS
  // Output is always in CENTS

  // 1) Compute total of all products (in input units - dollars for edit, cents for read)
  const totalProducts = prods.reduce(
    (acc, p) => ({
      list: acc.list + p.quantity * +(p.list_price || 0),
      sale: acc.sale + p.quantity * +(p.unit_price || 0),
      install: acc.install + p.quantity * +(p.install_price || 0),
    }),
    { list: 0, sale: 0, install: 0 }
  );
  
  // 2) Get only taxable products total
  const taxableProductsTotal = prods
    .filter(p => p.taxable)
    .reduce(
      (acc, p) => ({
        list: acc.list + p.quantity * +(p.list_price || 0),
        sale: acc.sale + p.quantity * +(p.unit_price || 0),
        install: acc.install + p.quantity * +(p.install_price || 0),
      }),
      { list: 0, sale: 0, install: 0 }
    );

  // 3) Calculate total fees and discounts (non-compounding against total products)
  const feesAndDiscounts = allTaxes
    .filter(t => t.tax_type === "fee" || t.tax_type === "discount");

  let totalListFeesDiscounts = 0;
  let totalSaleFeesDiscounts = 0;
  let totalInstallFeesDiscounts = 0;

  for (const tax of feesAndDiscounts) {
    const listRate = tax.list_rate || 0;
    const saleRate = tax.sale_rate || 0;
    const installRate = tax.install_rate || 0;

    if (tax.list_type === "fixed") {
      // Fixed rate is in cents; convert to dollars if isEdit (products in dollars)
      totalListFeesDiscounts += isEdit ? listRate / 100 : listRate;
    } else {
      totalListFeesDiscounts += (listRate / 100) * totalProducts.list;
    }

    if (tax.sale_type === "fixed") {
      totalSaleFeesDiscounts += isEdit ? saleRate / 100 : saleRate;
    } else {
      totalSaleFeesDiscounts += (saleRate / 100) * totalProducts.sale;
    }

    if (tax.install_type === "fixed") {
      totalInstallFeesDiscounts += isEdit ? installRate / 100 : installRate;
    } else {
      totalInstallFeesDiscounts += (installRate / 100) * totalProducts.install;
    }
  }

  // 4) Calculate taxable subtotal (taxable products + proportional fees/discounts)
  const remainingAfterDiscounts = {
    list: totalProducts.list + totalListFeesDiscounts,
    sale: totalProducts.sale + totalSaleFeesDiscounts,
    install: totalProducts.install + totalInstallFeesDiscounts
  };

  const taxableSubtotal = {
    list: totalProducts.list > 0 ? (taxableProductsTotal.list / totalProducts.list) * remainingAfterDiscounts.list : 0,
    sale: totalProducts.sale > 0 ? (taxableProductsTotal.sale / totalProducts.sale) * remainingAfterDiscounts.sale : 0,
    install: totalProducts.install > 0 ? (taxableProductsTotal.install / totalProducts.install) * remainingAfterDiscounts.install : 0
  };

  // 5) Calculate taxes - round each individual tax before accumulating
  // All calculations done in CENTS to avoid floating point issues
  const onlyTaxes = allTaxes.filter(t => t.tax_type === "tax");
  let totalListTaxesCents = 0;
  let totalSaleTaxesCents = 0;
  let totalInstallTaxesCents = 0;

  for (const tax of onlyTaxes) {
    const listRate = tax.list_rate || 0;
    const saleRate = tax.sale_rate || 0;
    const installRate = tax.install_rate || 0;

    // For isEdit: taxableSubtotal is in dollars, need to multiply by 100 for cents
    // For !isEdit: taxableSubtotal is in cents already
    const multiplier = isEdit ? 100 : 1;

    if (tax.list_type === "fixed") {
      totalListTaxesCents += Math.round(listRate); // Already in cents
    } else {
      totalListTaxesCents += Math.round((listRate / 100) * taxableSubtotal.list * multiplier);
    }

    if (tax.sale_type === "fixed") {
      totalSaleTaxesCents += Math.round(saleRate);
    } else {
      totalSaleTaxesCents += Math.round((saleRate / 100) * taxableSubtotal.sale * multiplier);
    }

    if (tax.install_type === "fixed") {
      totalInstallTaxesCents += Math.round(installRate);
    } else {
      totalInstallTaxesCents += Math.round((installRate / 100) * taxableSubtotal.install * multiplier);
    }
  }

  // 6) Round fees/discounts individually as well (in cents)
  let totalListFeesDiscountsCents = 0;
  let totalSaleFeesDiscountsCents = 0;
  let totalInstallFeesDiscountsCents = 0;

  for (const tax of feesAndDiscounts) {
    const listRate = tax.list_rate || 0;
    const saleRate = tax.sale_rate || 0;
    const installRate = tax.install_rate || 0;

    const multiplier = isEdit ? 100 : 1;

    if (tax.list_type === "fixed") {
      totalListFeesDiscountsCents += Math.round(listRate);
    } else {
      totalListFeesDiscountsCents += Math.round((listRate / 100) * totalProducts.list * multiplier);
    }

    if (tax.sale_type === "fixed") {
      totalSaleFeesDiscountsCents += Math.round(saleRate);
    } else {
      totalSaleFeesDiscountsCents += Math.round((saleRate / 100) * totalProducts.sale * multiplier);
    }

    if (tax.install_type === "fixed") {
      totalInstallFeesDiscountsCents += Math.round(installRate);
    } else {
      totalInstallFeesDiscountsCents += Math.round((installRate / 100) * totalProducts.install * multiplier);
    }
  }

  // 7) Combine fees/discounts + taxes (all in cents)
  const totalTaxCents = {
    list: totalListFeesDiscountsCents + totalListTaxesCents,
    sale: totalSaleFeesDiscountsCents + totalSaleTaxesCents,
    install: totalInstallFeesDiscountsCents + totalInstallTaxesCents,
  };

  const safeTotalTaxCents = {
    list: isNaN(totalTaxCents.list) ? 0 : totalTaxCents.list,
    sale: isNaN(totalTaxCents.sale) ? 0 : totalTaxCents.sale,
    install: isNaN(totalTaxCents.install) ? 0 : totalTaxCents.install,
  };

  // 8) Always return cents
  return safeTotalTaxCents;
};

export const calculateAdjustedTaxableTotal = (
  data: TTargetTax[] | undefined,
  total: { list: number; sale: number; install: number },
  taxableTotal: { list: number; sale: number; install: number }
) => {
  if (!data || !data.length) {
    return {
      ...taxableTotal,
      feeDiscountOnly: { list: 0, sale: 0, install: 0 },
      allProductsWithFeesDiscounts: {
        list: total.list,
        sale: total.sale,
        install: total.install,
      },
      feeDiscountOnlyAll: { list: 0, sale: 0, install: 0 },
    };
  }

  // Convert from cents to dollars for calculations
  const originalTotal = {
    list: total.list / 100,
    sale: total.sale / 100,
    install: total.install / 100
  };

  // Convert taxable total from cents to dollars
  const taxableOnly = {
    list: taxableTotal.list / 100,
    sale: taxableTotal.sale / 100,
    install: taxableTotal.install / 100
  };

  // Calculate fees and discounts against full subtotal (flat + percent, non-compounding)
  const feesAndDiscounts = data.filter(t => t.tax_type === "fee" || t.tax_type === "discount");

  let totalListFeesDiscountsAll = 0;
  let totalSaleFeesDiscountsAll = 0;
  let totalInstallFeesDiscountsAll = 0;

  for (const tax of feesAndDiscounts) {
    const listRate = tax.list_rate || 0;
    const saleRate = tax.sale_rate || 0;
    const installRate = tax.install_rate || 0;

    if (tax.list_type === "fixed") {
      totalListFeesDiscountsAll += listRate / 100;
    } else {
      totalListFeesDiscountsAll += (listRate / 100) * originalTotal.list;
    }

    if (tax.sale_type === "fixed") {
      totalSaleFeesDiscountsAll += saleRate / 100;
    } else {
      totalSaleFeesDiscountsAll += (saleRate / 100) * originalTotal.sale;
    }

    if (tax.install_type === "fixed") {
      totalInstallFeesDiscountsAll += installRate / 100;
    } else {
      totalInstallFeesDiscountsAll += (installRate / 100) * originalTotal.install;
    }
  }

  const adjustedAllProductsDollars = {
    list: originalTotal.list + totalListFeesDiscountsAll,
    sale: originalTotal.sale + totalSaleFeesDiscountsAll,
    install: originalTotal.install + totalInstallFeesDiscountsAll,
  };

  // Taxable subtotal = (taxable ratio) × (subtotal + all fees/discounts) so flat and percent are both reflected
  const taxableRatio = {
    list: originalTotal.list > 0 ? taxableOnly.list / originalTotal.list : 0,
    sale: originalTotal.sale > 0 ? taxableOnly.sale / originalTotal.sale : 0,
    install: originalTotal.install > 0 ? taxableOnly.install / originalTotal.install : 0,
  };

  const adjustedTaxableSubtotalDollars = {
    list: taxableRatio.list * adjustedAllProductsDollars.list,
    sale: taxableRatio.sale * adjustedAllProductsDollars.sale,
    install: taxableRatio.install * adjustedAllProductsDollars.install,
  };

  const feeDiscountOnlyDollars = {
    list: adjustedTaxableSubtotalDollars.list - taxableOnly.list,
    sale: adjustedTaxableSubtotalDollars.sale - taxableOnly.sale,
    install: adjustedTaxableSubtotalDollars.install - taxableOnly.install,
  };

  const result = {
    list: adjustedTaxableSubtotalDollars.list * 100,
    sale: adjustedTaxableSubtotalDollars.sale * 100,
    install: adjustedTaxableSubtotalDollars.install * 100,
    feeDiscountOnly: {
      list: feeDiscountOnlyDollars.list * 100,
      sale: feeDiscountOnlyDollars.sale * 100,
      install: feeDiscountOnlyDollars.install * 100,
    },
    allProductsWithFeesDiscounts: {
      list: adjustedAllProductsDollars.list * 100,
      sale: adjustedAllProductsDollars.sale * 100,
      install: adjustedAllProductsDollars.install * 100,
    },
  };

  return {
    list: isNaN(result.list) ? 0 : result.list,
    sale: isNaN(result.sale) ? 0 : result.sale,
    install: isNaN(result.install) ? 0 : result.install,
    feeDiscountOnly: {
      list: isNaN(result.feeDiscountOnly.list) ? 0 : result.feeDiscountOnly.list,
      sale: isNaN(result.feeDiscountOnly.sale) ? 0 : result.feeDiscountOnly.sale,
      install: isNaN(result.feeDiscountOnly.install) ? 0 : result.feeDiscountOnly.install,
    },
    allProductsWithFeesDiscounts: {
      list: isNaN(result.allProductsWithFeesDiscounts.list) ? 0 : result.allProductsWithFeesDiscounts.list,
      sale: isNaN(result.allProductsWithFeesDiscounts.sale) ? 0 : result.allProductsWithFeesDiscounts.sale,
      install: isNaN(result.allProductsWithFeesDiscounts.install) ? 0 : result.allProductsWithFeesDiscounts.install,
    },
  };
};